import { useReactiveVar } from '@apollo/client'
import {
  type GetUserFavouritesListQuery,
  AuthModalVariant,
  useGetUserFavouritesListQuery,
} from '@kijiji/generated/graphql-types'
import FavouriteOutlineThickIcon from '@kijiji/icons/src/icons/FavouriteOutlineThick'
import FavouriteOutlineThinIcon from '@kijiji/icons/src/icons/FavouriteOutlineThin'
import FavouriteSolidIcon from '@kijiji/icons/src/icons/FavouriteSolid'
import { type ThemeProps } from '@kijiji/theme'
import { useRouter } from 'next/router'
import { useSession } from 'next-auth/react'
import { useTranslation } from 'next-i18next'
import qs from 'query-string'
import { type FC, type MouseEvent } from 'react'
import { useTheme } from 'styled-components'

import { ListingFavouriteButton } from '@/components/shared/listing/styled'
import { TRANSLATION_KEYS } from '@/constants/localization'
import { useSignInModal } from '@/features/auth/hooks/useSignInModal'
import { useFavouriteListingUpdate } from '@/hooks/useFavouriteListingUpdate'
import { favouriteMap } from '@/lib/apollo/favouriteMap'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { trackEvent } from '@/lib/ga/utils/tracking'
import { Button } from '@/ui/atoms/button'

type AllowedColorSchemes = 'grey' | 'purple'
export type FavouriteButtonSize = 'large' | 'xLarge'
export type FavouriteButtonColorScheme = Extract<keyof ThemeProps['colors'], AllowedColorSchemes>

export type FavouriteButtonProps = {
  /**
   * The ID of the listing to favourite.
   */
  listingId: string

  /**
   * Simple is just the heart. Full includes label and outline around for the button.
   */
  variant?: 'simple' | 'simple-with-label' | 'full' | 'simple-with-background'

  /**
   * Specifies if it should use the thin or think favourite icon
   */
  iconVariant?: 'thin' | 'thick'

  /**
   * The color scheme of the button.
   * Note: This does not work with the 'full' variant.
   */
  colorScheme?: FavouriteButtonColorScheme

  /**
   * The size of the button.
   * Note: This does not work with the 'full' variant.
   */
  size?: FavouriteButtonSize

  isDisabled?: boolean
}

/**
 * This component implements a favourite button for a listing ID
 *
 * It should be used with the hook: "useFavouriteOnCallback"
 * This hook should be implemented once per page, not per "FavouriteButton" usage.
 */
export const FavouriteButton: FC<FavouriteButtonProps> = ({
  listingId,
  iconVariant = 'thick',
  colorScheme = 'grey',
  size = 'large',
  variant = 'simple',
  isDisabled,
}) => {
  /**
   * TODO: The favourite button will need some refactoring to clean up, we're using a mix of Apollo
   * Client and Reactive Variables to manage the favourite state. This is a temporary solution to
   * avoid having to do long lookups in the cache to determine if a listing is favourited or not.
   *
   * See https://jira.ets.mpi-internal.com/browse/KJCA-4885
   */
  const FavouriteOutlineIcon =
    iconVariant === 'thick' ? FavouriteOutlineThickIcon : FavouriteOutlineThinIcon

  const { t } = useTranslation(TRANSLATION_KEYS.FAVOURITES)
  const { spacing } = useTheme()

  const { data: userData } = useSession()
  const { openSignInModal } = useSignInModal()
  const { updateFavourite, updateLoading } = useFavouriteListingUpdate()
  const { asPath } = useRouter()

  /** USE THIS SPARINGLY. See the comment under favouriteMap.ts for details. */
  const reactiveFavouriteMap = useReactiveVar(favouriteMap)

  const isFavourite = reactiveFavouriteMap[listingId] ?? false
  const setIsFavourite = (state: boolean) => {
    favouriteMap({ ...favouriteMap(), [listingId]: state })
  }

  const { refetch } = useGetUserFavouritesListQuery({
    ssr: false,
    skip: !userData?.user.sub,
    fetchPolicy: 'cache-first',
    onCompleted: (data: GetUserFavouritesListQuery) => {
      if (data.favouriteListingsIds && data.favouriteListingsIds.includes(listingId)) {
        setIsFavourite(true)
      }
    },
  })

  const handleToggleFavourite = (event: MouseEvent<HTMLButtonElement>) => {
    /**
     * Prevents a link redirect to be triggered when used inside of a listing card
     * */
    event.preventDefault()
    event.stopPropagation()

    if (updateLoading) {
      return
    }

    if (!userData?.user) {
      const { url, query: queryParams } = qs.parseUrl(asPath)
      queryParams.listingId = listingId
      const callbackUrl = qs.stringifyUrl({ url, query: queryParams })

      openSignInModal({ modalVariant: AuthModalVariant.LoginToFavourite, callbackUrl })

      return
    }

    setIsFavourite(!isFavourite)

    updateFavourite(isFavourite, {
      variables: { listingId: listingId.toString() },
      onError: (error) => {
        // Catches off case a listing is already favourited resulting in an error.
        // This is a good indication to refetch the list of favourite ids
        if (error.message.includes('already favourited')) {
          refetch()
          return
        }
        setIsFavourite(isFavourite)
      },
      onCompleted: () => {
        trackEvent({
          action: (isFavourite && GA_EVENT.WatchlistRemove) || GA_EVENT.WatchlistAdd,
          label: `adId=${listingId}`,
        })
      },
      onQueryUpdated: () => false, // the trick to preventing a refetch of main query after mutation
    })
  }

  const geFavouritedSolidContent = (
    isFavourite: boolean,
    variant: FavouriteButtonProps['variant']
  ) => {
    if (variant === 'full' || variant === 'simple-with-label') {
      return isFavourite ? (
        <>
          <FavouriteSolidIcon aria-hidden size="xsm" />
          <span>{t('favourites.button.text.saved')}</span>
        </>
      ) : (
        <>
          <FavouriteOutlineIcon aria-hidden size="xsm" />
          <span>{t('favourites.button.text.save')}</span>
        </>
      )
    }

    return isFavourite ? <FavouriteSolidIcon aria-hidden /> : <FavouriteOutlineIcon aria-hidden />
  }

  if (variant === 'full') {
    return (
      <Button
        disabled={isDisabled}
        variant="secondary"
        size="small"
        title={`${t('favourites.button.click_to_favourite')}`}
        aria-label={
          isFavourite
            ? `${t('favourites.button.label_remove')}`
            : `${t('favourites.button.label_add')}`
        }
        aria-pressed={isFavourite}
        data-testid="listing-favourite-button"
        onClick={handleToggleFavourite}
        type="button"
        gap={spacing.mini}
        isLoading={updateLoading}
      >
        {geFavouritedSolidContent(isFavourite, variant)}
      </Button>
    )
  }

  return (
    <ListingFavouriteButton
      disabled={isDisabled}
      title={`${t('favourites.button.click_to_favourite')}`}
      aria-label={
        isFavourite
          ? `${t('favourites.button.label_remove')}`
          : `${t('favourites.button.label_add')}`
      }
      aria-pressed={isFavourite}
      data-testid="listing-favourite-button"
      onClick={handleToggleFavourite}
      type="button"
      colorScheme={colorScheme}
      size={size}
      variant={variant}
    >
      {geFavouritedSolidContent(isFavourite, variant)}
    </ListingFavouriteButton>
  )
}
